import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { RootState } from "app/store";
import { ApiResponse } from "models";
import { inVoiceState } from "models/inVoice";

const initialState: inVoiceState = {
  loading: false,
  data: {},
  message: "",
  success: false,
  pdfLoading: false,
  pdfMessage: "",
  pdfSuccess: false,
  pdfData: null,
  pendingLoading: false,
  pendingMessage: "",
  pendingSuccess: false,
  pendingData: [],
  sendLoading: false,
  sendMessage: "",
  sendSuccess: false,
};

const invoiceSlice = createSlice({
  name: "invoice",
  initialState,
  reducers: {
    getInvoice(state, action: PayloadAction<any>) {
      state.loading = true;
      state.message = "";
    },
    getInvoiceSuccess(state, action: PayloadAction<ApiResponse>) {
      state.loading = false;
      state.data = action.payload;
      state.success = action.payload?.success;
    },
    getInvoiceFailed(state, action: PayloadAction<ApiResponse>) {
      state.loading = false;
      state.data = {};
      state.message = action.payload?.message;
      state.success = action.payload?.success;
    },
    updateInvoice(state, action: PayloadAction<ApiResponse>) {
      state.updateLoading = true;
      state.message = "";
    },
    updateInvoiceSuccess(state, action: PayloadAction<ApiResponse>) {
      state.updateLoading = false;
      state.updateMessage = action.payload.message;
      state.success = action.payload.success;
    },
    updateInvoiceFailed(state, action: PayloadAction<ApiResponse>) {
      state.updateLoading = false;
      state.updateMessage = action.payload.message;
      state.success = action.payload.success;
    },
    editInvoice(state, action: PayloadAction<any>) {
      state.editLoading = true;
      state.message = "";
    },
    editInvoiceSuccess(state, action: PayloadAction<ApiResponse>) {
      state.editLoading = false;
      state.message = action.payload.message;
      state.success = action.payload.success;
    },
    editInvoiceFailed(state, action: PayloadAction<ApiResponse>) {
      state.editLoading = false;
      state.message = action.payload.message;
      state.success = action.payload.success;
    },
    voidInvoice: (
      state,
      action: PayloadAction<{ invoiceId: string; voidReason: string }>
    ) => {
      state.loading = true;
      state.message = "";
    },

    voidInvoiceSuccess: (state, action: PayloadAction<ApiResponse>) => {
      state.loading = false;
      state.message = action.payload.message;
      state.success = action.payload.success;
    },

    voidInvoiceFailed: (state, action: PayloadAction<ApiResponse>) => {
      state.loading = false;
      state.message = action.payload.message;
      state.success = action.payload.success;
    },
    deleteInvoice: (state, action: PayloadAction<{ invoiceId: string }>) => {
      state.loading = true;
      state.message = "";
    },

    deleteInvoiceSuccess: (state, action: PayloadAction<ApiResponse>) => {
      state.loading = false;
      state.message = action.payload.message;
      state.success = action.payload.success;
    },
    getPendingInvoices(state, action: PayloadAction<any>) {
      state.pendingLoading = true;
      state.pendingMessage = "";
      state.pendingSuccess = false;
    },
    getPendingInvoicesSuccess(state, action: PayloadAction<ApiResponse>) {
      state.pendingLoading = false;
      state.pendingMessage = action.payload.message;
      state.pendingSuccess = action.payload.success;
      state.pendingData = action.payload || [];
    },
    getPendingInvoicesFailed(state, action: PayloadAction<ApiResponse>) {
      state.pendingLoading = false;
      state.pendingMessage = action.payload.message;
      state.pendingSuccess = false;
      state.pendingData = [];
    },

    deleteInvoiceFailed: (state, action: PayloadAction<ApiResponse>) => {
      state.loading = false;
      state.message = action.payload.message;
      state.success = action.payload.success;
    },
    getInvoicePdfRequest(state, action: PayloadAction<{ invoiceId: string }>) {
      state.pdfLoading = true;
      state.pdfMessage = "";
      state.pdfSuccess = false;
      state.pdfData = null;
    },
    getInvoicePdfSuccess(state, action: PayloadAction<ApiResponse>) {
      state.pdfLoading = false;
      state.pdfMessage = action.payload.message || "PDF request successful";
      state.pdfSuccess = action.payload.success ?? true;
      state.pdfData = action.payload || null;
    },
    getInvoicePdfFailed(state, action: PayloadAction<ApiResponse>) {
      state.pdfLoading = false;
      state.pdfMessage = action.payload.message;
      state.pdfSuccess = false;
      state.pdfData = null;
    },
    resetInvoicePdf(state) {
      state.pdfLoading = false;
      state.pdfSuccess = false;
      state.pdfData = null;
      state.pdfMessage = "";
    },

    clearData(state) {
      state.loading = false;
      state.message = "";
      state.success = false;
      state.deleteLoading = false;
      state.updateMessage = "";
      state.deleteMessage = "";
      state.imageUrl = "";
      state.editMessage = "";
      state.editLoading = false;
    },

    sendInvoice(
      state,
      action: PayloadAction<{ invoiceId: string; isEmail?: boolean; isWhatsapp?: boolean }>
    ) {
      state.sendLoading = true;
      state.sendMessage = "";
      state.sendSuccess = false;
    },
    sendInvoiceSuccess(state, action: PayloadAction<ApiResponse>) {
      state.sendLoading = false;
      state.sendMessage = action.payload.message;
      state.sendSuccess = action.payload.success;
    },
    sendInvoiceFailed(state, action: PayloadAction<ApiResponse>) {
      state.sendLoading = false;
      state.sendMessage = action.payload.message;
      state.sendSuccess = false;
    },
  },
});

// Actions
export const invoiceAction = invoiceSlice.actions;

// Selectors
export const getInvoiceData = (state: RootState) => state.inVoiceModule.data;
export const selectIsLoading = (state: RootState) =>
  state.inVoiceModule.loading;
export const selectIsMessage = (state: RootState) =>
  state.inVoiceModule.message;
export const updateLoading = (state: RootState) =>
  state.inVoiceModule.updateLoading;
export const updateMessage = (state: RootState) =>
  state.inVoiceModule.updateMessage;
export const editLoading = (state: RootState) =>
  state.inVoiceModule.editLoading;
export const editMessage = (state: RootState) =>
  state.inVoiceModule.editMessage;

export const selectPdfLoading = (state: RootState) =>
  state.inVoiceModule.pdfLoading;
export const selectPdfMessage = (state: RootState) =>
  state.inVoiceModule.pdfMessage;
export const selectPdfSuccess = (state: RootState) =>
  state.inVoiceModule.pdfSuccess;
export const selectPdfData = (state: RootState) => state.inVoiceModule.pdfData;
export const selectSendLoading = (state: RootState) =>
  state.inVoiceModule.sendLoading;
export const selectSendMessage = (state: RootState) =>
  state.inVoiceModule.sendMessage;
export const selectSendSuccess = (state: RootState) =>
  state.inVoiceModule.sendSuccess;

export const selectPendingLoading = (state: RootState) =>
  state.inVoiceModule.pendingLoading;
export const selectPendingMessage = (state: RootState) =>
  state.inVoiceModule.pendingMessage;
export const selectPendingSuccess = (state: RootState) =>
  state.inVoiceModule.pendingSuccess;
export const selectPendingData = (state: RootState) =>
  state.inVoiceModule.pendingData;
// Reducer
const invoiceReducer = invoiceSlice.reducer;
export default invoiceReducer;
